/**
 * A loading placeholder shaped like the thing that is coming.
 *
 * The point is not decoration — it is that the layout does not jump. A centred
 * spinner is replaced by a full page, so everything moves at the moment the data
 * lands; a skeleton is replaced in place.
 */
export function Skeleton({
  width,
  height = 12,
  radius = 6,
  className = '',
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`skeleton block ${className}`}
      style={{ width: width ?? '100%', height, borderRadius: radius }}
    />
  );
}

/**
 * Wraps a set of skeletons so assistive tech announces "loading" ONCE, instead of
 * reading out however many grey rectangles happen to be on screen.
 */
export function SkeletonGroup({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div role="status" aria-label={label} aria-busy className={className}>
      {children}
    </div>
  );
}

/** One placeholder card, matching TaskCard's real proportions. */
export function TaskCardSkeleton({ lines = 2 }: { lines?: number }) {
  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
    >
      <div className="flex gap-1.5">
        <Skeleton width={62} height={16} radius={5} />
        <Skeleton width={42} height={16} radius={5} />
      </div>

      <div className="mt-2.5 flex flex-col gap-1.5">
        <Skeleton height={11} />
        {lines > 1 && <Skeleton width="70%" height={11} />}
      </div>

      <div
        className="mt-3 flex items-center gap-2 border-t pt-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <Skeleton width={34} height={9} />
        <Skeleton width={18} height={9} className="ml-auto" />
      </div>
    </div>
  );
}
